// ink-component
import React from 'react';
import styled, { css } from 'styled-components';

import CoreRow from '../../_common/CoreRow';
import { StyledHeader } from '../NewTableHeadCell/NewTableHeadCell';
import { EquityVariants } from '../../../_common/Types/GlobalTypes';
import { newTableTokens } from '../../../../tokens/components/NewTable/tokens';

const validColors = [
  'green',
  'red',
  'blue',
  'orange',
  'yellow',
  'gray',
  'purple',
  'pink',
  'green-light',
  'red-light',
  'blue-light',
  'orange-light',
  'yellow-light',
  'gray-light',
  'purple-light',
  'pink-light',
  'brown',
] as const;

export interface NewTableStripeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Semantic variant, based on intent */
  variant?: EquityVariants;
  /** Defines the color of the stripe */
  color?: typeof validColors[number];
}

const stripeVariants = {
  'equity-common': newTableTokens.StripeBackgroundColorCommonDefault,
  'equity-preferred': newTableTokens.StripeBackgroundColorPreferredDefault,
  'equity-convertible': newTableTokens.StripeBackgroundColorConvertibleDefault,
  'equity-warrant': newTableTokens.StripeBackgroundColorWarrantDefault,
  'equity-option': newTableTokens.StripeBackgroundColorOptionDefault,
  'equity-pool': newTableTokens.StripeBackgroundColorPoolDefault,
};

const stripeColors = {
  green: newTableTokens.StripeBackgroundColorGreenDefault,
  red: newTableTokens.StripeBackgroundColorRedDefault,
  blue: newTableTokens.StripeBackgroundColorBlueDefault,
  orange: newTableTokens.StripeBackgroundColorOrangeDefault,
  yellow: newTableTokens.StripeBackgroundColorYellowDefault,
  gray: newTableTokens.StripeBackgroundColorGrayDefault,
  purple: newTableTokens.StripeBackgroundColorPurpleDefault,
  pink: newTableTokens.StripeBackgroundColorPinkDefault,
  'green-light': newTableTokens.StripeBackgroundColorGreenLightDefault,
  'red-light': newTableTokens.StripeBackgroundColorRedLightDefault,
  'blue-light': newTableTokens.StripeBackgroundColorBlueLightDefault,
  'orange-light': newTableTokens.StripeBackgroundColorOrangeLightDefault,
  'yellow-light': newTableTokens.StripeBackgroundColorYellowLightDefault,
  'gray-light': newTableTokens.StripeBackgroundColorGrayLightDefault,
  'purple-light': newTableTokens.StripeBackgroundColorPurpleLightDefault,
  'pink-light': newTableTokens.StripeBackgroundColorPinkLightDefault,
  brown: newTableTokens.StripeBackgroundColorBrownDefault,
};

const StyledStripe = styled.div<NewTableStripeProps>`
  ${({ theme }) =>
    css`
      background-color: ${stripeColors[theme.color]};
    `}

  ${({ theme }) =>
    theme.variant !== undefined &&
    css`
      background-color: ${stripeVariants[theme.variant]};
    `}

  bottom: -1px;
  left: 0;
  min-width: 4px;
  position: absolute;
  top: 0;
  width: 4px;
  z-index: 1;

  tbody:last-child ${CoreRow}:last-child && {
    bottom: 0;
  }

  ${StyledHeader} && {
    display: none;
  }
`;

/**
 * @group NewTable
 * @purpose Visually group rows by relevant data
 * @categories table
 * @HTMLType HTMLDivElement
 */
function NewTableStripe(props: NewTableStripeProps) {
  const { style, className, color = 'blue', variant, ...otherProps } = props;

  if (!validColors.includes(color)) {
    throw new Error(
      "Prop `color` needs to be one of the listed colors: `green`, `red`, `blue`, `orange`, `yellow`, `gray`, `purple`, `pink`, `green-light`, `red-light`, `blue-light`, `orange-light`, `yellow-light`, `gray-light`, `purple-light`, `pink-light`, or `brown`. Consider using the `variant` prop if you're working with equity colors.",
    );
  }

  return <StyledStripe theme={{ color, variant }} {...otherProps} />;
}

NewTableStripe.defaultProps = {
  color: 'blue',
};

NewTableStripe.displayName = 'NewTable.Stripe';

export default NewTableStripe;
